import { ref, type Ref } from "vue";
import { UploadDeliveryAreaSectionDocument } from "@/generated/graphql";
import {
  localStorageGetChainCode,
  localStorageGetCustomerCode,
  localStorageGetUserId,
} from "@/common/localStorageUtil";
import { CsvFileUpload, FileReaderParams } from "@/common/fileUpload";
import type { ChainSelectValue } from "../types";

/**
 * composableへ渡す依存をまとめた型。
 */
interface UseAreaListUploadParams {
  overlay: Ref<boolean>;
  uploadDialog: Ref<boolean>;
  messagedialog: Ref<boolean>;
  message: Ref<string>;
  chainSelectValues: Ref<ChainSelectValue>;
}

/**
 * 画面ロジックを扱う composable。
 */
export const useAreaListUpload = ({
  overlay,
  uploadDialog,
  messagedialog,
  message,
  chainSelectValues,
}: UseAreaListUploadParams) => {
  const fileInput = ref<HTMLInputElement | null>(null);

  // CSV アップロード用パラメータ作成
  const getCsvFileUploadParams = (file: File): FileReaderParams => {
    return {
      file,
      createUploadInput: (base64: string, chainCode: string) => ({
        customer_code: localStorageGetCustomerCode(),
        chain_code: chainCode,
        csv: base64,
        current_chain_code: localStorageGetChainCode(),
        user_id: localStorageGetUserId(),
      }),
      mutationDocument: UploadDeliveryAreaSectionDocument,
      resultDataKey: "uploadDeliveryAreaSection",
      chainCode: chainSelectValues.value.chain_code,
      csv_split_unit: 50, // 50行ずつ処理する
      handleUploadComplete: (_failed, msg) => {
        messagedialog.value = true;
        message.value = msg;
        uploadDialog.value = false;
      },
      handleError: (_err, msg) => {
        overlay.value = false;
        messagedialog.value = true;
        message.value = msg;
        uploadDialog.value = false;
      },
    };
  };

  // CSV アップロード
  const uploadCsvFile = async (file: File): Promise<void> => {
    const params = getCsvFileUploadParams(file);
    uploadDialog.value = true;
    await CsvFileUpload.execute(params);
  };

  // ファイル選択ハンドラ
  const onChangeFileUpload = async (e: Event): Promise<void> => {
    if (!chainSelectValues.value.chain_code) {
      messagedialog.value = true;
      message.value = "チェーン名を選択してください。";
    } else {
      const target = e.target as HTMLInputElement;
      const file = target.files?.[0];
      if (file) {
        await uploadCsvFile(file);
      }
    }
    // ファイル名をクリアする
    const inputRef = e.currentTarget as HTMLInputElement;
    inputRef.value = "";
  };

  return {
    fileInput,
    onChangeFileUpload,
  };
};
